import Button from '../shared/Button.js';
import React from 'react';
import {useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {recordSeqNavOptions} from '../lib/recordSequence.js';
import {buildRuminantWeighInSessionColumns} from '../lib/weighInSessionExports.js';
import {csvFilename, downloadCsv, rowsToCsv} from '../lib/csvExport.js';
import {printRows} from '../lib/printExport.js';
import {createSavedView, updateSavedView, deleteSavedView} from '../lib/savedViewsApi.js';
import {savedViewsErrorMessage, useSavedViewsData} from '../shared/savedViewsData.js';
import CattleNewWeighInModal from './CattleNewWeighInModal.jsx';
import {
  avgAdgProvenance,
  buildCattleWeighInListMetrics,
  cattleWeighInListMetrics,
  formatAvgAdg,
  formatDaysSinceLastWeighIn,
} from './cattleWeighInListMetrics.js';
import {createWeighInSession} from '../lib/weighInApi.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {CattleWeighInsListData} from '../app/data/routeDataCatalog.js';
import {useRouteResource, type UseRouteResourceReturn} from '../app/data/useRouteResource.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import DataTable from '../shared/DataTable.tsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import StatusText from '../shared/StatusText.jsx';
import Badge from '../shared/Badge.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import InlineNotice, {type Notice} from '../shared/InlineNotice.jsx';
import {usePersistentViewState} from '../lib/usePersistentViewState.js';
import {getProgramColor} from '../lib/programColors.js';
import {sb} from '../lib/supabase.js';
import {fmt, fmtDate} from '../lib/dateUtils.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import {useUI} from '../contexts/UIContext.jsx';
import Header from '../shared/Header.tsx';
import {CATTLE_HERD_KEYS} from '../lib/cattleHerdFilters.js';
import {
  RuminantGroupLabel,
  RuminantWeighInsHeaderSummary,
  ruminantSavedViewInputStyle,
  ruminantSavedViewRadioLabelStyle,
  ruminantWeighInEmptyBoxStyle,
  ruminantWeighInEntryMatchText,
} from './ruminantPresentation.js';
import './cattleRedesign.css';
import './cattleWeighInsRedesign.css';

type CattleRecord = Record<string, any>;
type LockedTeamMemberSource = {
  name?: string | null;
  profile?: {name?: string | null; full_name?: string | null} | null;
  user?: {email?: string | null} | null;
};

const HERD_LABELS: Record<string, string> = {
  mommas: 'Mommas',
  backgrounders: 'Backgrounders',
  finishers: 'Finishers',
  bulls: 'Bulls',
};
const CATTLE_WEIGHINS_SURFACE_KEY = 'cattle.weighins';
const CATTLE_WEIGHINS_PARAMS = Object.freeze({});
const VALID_WEIGHIN_STATUS_FILTERS = new Set(['all', 'draft', 'complete']);
const EXTENDED_LIST_CONTROLS_ENABLED = false;
// Stable empties. A fresh `[]` / `{}` per render would give the analysis memo
// below a new dependency identity every time, so it would rebuild the whole
// rolling walk on every render — the exact cost the memo exists to avoid.
const NO_SESSIONS: CattleRecord[] = [];
const NO_ENTRIES: Record<string, CattleRecord[]> = {};

const CattleWeighInsView = () => {
  const {authState} = useAuth();
  const {setView} = useUI();
  const navigate = useNavigate();
  const {useState, useEffect} = React;
  type ErrorLike = {message?: string | null};
  const cattleWeighIns = useRouteResource(RESOURCE.CATTLE_WEIGHINS_LIST, CATTLE_WEIGHINS_PARAMS) as Omit<
    UseRouteResourceReturn,
    'data' | 'error'
  > & {
    data: CattleWeighInsListData | null;
    error: ErrorLike | null;
  };
  const sessions: CattleRecord[] = cattleWeighIns.data ? cattleWeighIns.data.sessions : NO_SESSIONS;
  const entries: Record<string, CattleRecord[]> = cattleWeighIns.data ? cattleWeighIns.data.entries : NO_ENTRIES;
  const loading = cattleWeighIns.loading;
  const loadError: Notice | null = cattleWeighIns.error
    ? {
        kind: 'error',
        message:
          'Could not load cattle weigh-in sessions. Please refresh the page. (' +
          (cattleWeighIns.error.message || String(cattleWeighIns.error)) +
          ')',
      }
    : null;
  const loadAll = cattleWeighIns.refetch;
  // ONE rolling pass over the payload this resource already returned, keyed by
  // session id. Memoized on the payload rather than computed per rendered row:
  // the walk is O(S log S + E) for the WHOLE list, so doing it per row would
  // rescan the entire history once per session for no additional information.
  const listMetrics = React.useMemo(() => buildCattleWeighInListMetrics(sessions, entries), [sessions, entries]);
  const [statusFilter, setStatusFilter] = usePersistentViewState('cattle.weighins.statusFilter', 'all');
  const [showNewModal, setShowNewModal] = useState(false);
  const [tagSearch, setTagSearch] = usePersistentViewState('cattle.weighins.tagSearch', '');
  const [notice, setNotice] = useState<Notice | null>(null);
  const [selectedViewId, setSelectedViewId] = useState('');
  const [showSaveViewForm, setShowSaveViewForm] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');
  const [saveViewVisibility, setSaveViewVisibility] = useState('private');
  const [savedViewBusy, setSavedViewBusy] = useState(false);
  const [savedViewNotice, setSavedViewNotice] = useState<Notice | null>(null);
  const authRecord = authState as CattleRecord;
  const myProfileId = authRecord?.user?.id || null;
  const savedViewsQuery = useSavedViewsData(CATTLE_WEIGHINS_SURFACE_KEY, {client: sb});
  const savedViews = savedViewsQuery.data as CattleRecord[];
  const savedViewsError = savedViewsQuery.error ? savedViewsErrorMessage(savedViewsQuery.error) : null;
  const savedViewsLoading = savedViewsQuery.loading;
  const loadSavedViews = savedViewsQuery.refetch;
  const lockedAuthState: LockedTeamMemberSource | null =
    authState && typeof authState === 'object' ? (authState as LockedTeamMemberSource) : null;

  useEffect(() => {
    if (savedViewsLoading || savedViewsError) return;
    setSelectedViewId((cur) => (cur && savedViews.some((row) => row.id === cur) ? cur : ''));
  }, [savedViews, savedViewsError, savedViewsLoading]);

  async function createNewSession(opts: CattleRecord) {
    const id = 'wsess-' + String(Date.now()) + Math.random().toString(36).slice(2, 6);
    // Tier-2 write boundary (migration 196): weigh_in_sessions grants no
    // INSERT to authenticated. status/started_at are server-owned/defaulted
    // by the RPC (status is always created 'draft').
    const rec = {
      id,
      date: opts.date,
      team_member: opts.team_member,
      species: 'cattle',
      herd: opts.herd,
    };
    await createWeighInSession(sb, rec);
    setShowNewModal(false);
    navigate('/weigh-in-sessions/' + id);
  }

  const effectiveStatusFilter = EXTENDED_LIST_CONTROLS_ENABLED ? statusFilter : 'all';
  const effectiveTagSearch = EXTENDED_LIST_CONTROLS_ENABLED ? tagSearch : '';
  const tagQ = effectiveTagSearch.trim().toLowerCase();
  const entryMatchesTag = (e: CattleRecord) => !tagQ || (e.tag || '').toLowerCase().includes(tagQ);
  const statusFiltered = sessions.filter(
    (s: CattleRecord) => effectiveStatusFilter === 'all' || s.status === effectiveStatusFilter,
  );
  const filtered = tagQ ? statusFiltered.filter((s) => (entries[s.id] || []).some(entryMatchesTag)) : statusFiltered;
  const totalEntries = filtered.reduce((s, sess) => s + (entries[sess.id] || []).length, 0);
  const matchedSessionCount = tagQ ? filtered.length : null;
  const loadFailed = !!loadError;
  const selectedView = savedViews.find((v) => v.id === selectedViewId) || null;
  const selectedViewIsMine = !!(selectedView && myProfileId && selectedView.owner_profile_id === myProfileId);
  const emptyStateKind = sessions.length === 0 ? 'none' : 'filtered';
  const emptyStateMessage =
    emptyStateKind === 'none'
      ? 'No cattle weigh-in sessions yet.'
      : tagQ
        ? 'No cattle weigh-in sessions match #' + tagSearch + '.'
        : 'No cattle weigh-in sessions match the current filters.';
  const emptyStateHint =
    emptyStateKind === 'none'
      ? 'Click New Weigh-In to start one.'
      : tagQ
        ? 'Clear the tag search or switch back to All.'
        : 'Switch back to All to see every session.';

  function cattleWeighInsViewState() {
    return {
      statusFilter: VALID_WEIGHIN_STATUS_FILTERS.has(statusFilter) ? statusFilter : 'all',
      tagSearch: tagSearch || '',
    };
  }

  function applyCattleSavedView(view: CattleRecord | null | undefined) {
    if (!view) return;
    const st = (view.view_state || {}) as CattleRecord;
    setStatusFilter(VALID_WEIGHIN_STATUS_FILTERS.has(st.statusFilter) ? st.statusFilter : 'all');
    setTagSearch(typeof st.tagSearch === 'string' ? st.tagSearch : '');
    setSavedViewNotice(null);
  }

  function onSelectSavedView(id: string) {
    setSelectedViewId(id);
    setSavedViewNotice(null);
    if (!id) return;
    applyCattleSavedView(savedViews.find((v) => v.id === id));
  }

  function openSaveViewForm() {
    setSaveViewName('');
    setSaveViewVisibility('private');
    setSavedViewNotice(null);
    setShowSaveViewForm(true);
  }

  async function submitSaveView() {
    const name = saveViewName.trim();
    if (!name) {
      setSavedViewNotice({kind: 'error', message: 'Name the view before saving.'});
      return;
    }
    setSavedViewBusy(true);
    try {
      const created = (await createSavedView(sb, {
        surfaceKey: CATTLE_WEIGHINS_SURFACE_KEY,
        name,
        visibility: saveViewVisibility,
        viewState: cattleWeighInsViewState(),
      })) as CattleRecord;
      setShowSaveViewForm(false);
      setSavedViewNotice({kind: 'success', message: 'Saved view "' + name + '".'});
      await loadSavedViews();
      if (created?.id) setSelectedViewId(created.id);
    } catch (e) {
      const error = e as CattleRecord;
      setSavedViewNotice({kind: 'error', message: 'Save view failed: ' + (error.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function updateSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    setSavedViewBusy(true);
    try {
      await updateSavedView(sb, selectedView.id, {viewState: cattleWeighInsViewState()});
      await loadSavedViews();
      setSavedViewNotice({
        kind: 'success',
        message: 'Updated "' + selectedView.name + '" to the current filter/search.',
      });
    } catch (e) {
      const error = e as CattleRecord;
      setSavedViewNotice({kind: 'error', message: 'Update view failed: ' + (error.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function proceedDeleteSelectedView(view: CattleRecord) {
    setSavedViewBusy(true);
    try {
      await deleteSavedView(sb, view.id);
      setSelectedViewId('');
      await loadSavedViews();
      setSavedViewNotice({kind: 'success', message: 'Deleted saved view "' + view.name + '".'});
    } catch (e) {
      const error = e as CattleRecord;
      setSavedViewNotice({kind: 'error', message: 'Delete view failed: ' + (error.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  function deleteSelectedView() {
    if (!selectedView || !selectedViewIsMine || !window._wcfConfirmDelete) return;
    window._wcfConfirmDelete('Delete saved view "' + selectedView.name + '"?', () => {
      void proceedDeleteSelectedView(selectedView);
    });
  }

  // CSV export of the CURRENT result set (status filter + tag search applied).
  // Exports `filtered`, not raw `sessions`. Mirrors SheepWeighInsView. Session
  // counts come from entries[s.id]; matching-tag count only when a tag search
  // is active. Mechanics via the shared csvExport owner.
  function cattleWeighInsExportColumns() {
    return buildRuminantWeighInSessionColumns({
      groupHeader: 'Herd',
      groupLabels: HERD_LABELS,
      entriesBySession: entries,
      tagQ,
      entryMatchesTag,
    });
  }

  function handleExportCsv() {
    const columns = cattleWeighInsExportColumns();
    const ok = downloadCsv(csvFilename('cattle-weigh-in-sessions'), rowsToCsv(columns, filtered));
    if (!ok) setNotice({kind: 'error', message: 'CSV export is only available in the browser.'});
  }

  function handlePrintRows() {
    const columns = cattleWeighInsExportColumns();
    const ok = printRows({
      title: 'Cattle Weigh-In Sessions',
      subtitle: filtered.length + ' filtered weigh-in sessions',
      columns,
      rows: filtered,
    });
    if (!ok) setNotice({kind: 'error', message: 'Print is only available in the browser.'});
  }

  const myViews = savedViews.filter((v: CattleRecord) => myProfileId && v.owner_profile_id === myProfileId);
  const publicOtherViews = savedViews.filter(
    (v: CattleRecord) => v.visibility === 'public' && !(myProfileId && v.owner_profile_id === myProfileId),
  );
  const savedViewInputS = ruminantSavedViewInputStyle;
  const savedViewRadioLabelS = ruminantSavedViewRadioLabelStyle;

  return (
    <div
      className="cattle-redesign"
      style={{minHeight: '100vh', background: 'var(--bg-page)'}}
      data-weighin-list-loaded={loading || loadFailed ? 'false' : 'true'}
    >
      <Header />
      <div className="cattle-page cattle-weighins-page">
        <InlineNotice notice={loadError || notice} />
        {loadFailed && (
          <Button variant="secondary" type="button" onClick={loadAll} style={{marginBottom: 12}}>
            Retry
          </Button>
        )}
        {EXTENDED_LIST_CONTROLS_ENABLED && !loadFailed && (
          <>
            <div
              data-cattle-weighins-saved-views-row
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '10px 14px',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Saved views</span>
              {savedViewsError ? (
                <span style={{fontSize: 12, color: 'var(--danger-ink)'}} data-cattle-weighins-saved-views-error>
                  Saved views unavailable. Filters still work.
                </span>
              ) : (
                <>
                  <select
                    data-cattle-weighins-saved-view-select
                    value={selectedViewId}
                    disabled={savedViewsLoading}
                    onChange={(e) => onSelectSavedView(e.target.value)}
                    style={{...savedViewInputS, width: 'auto', minWidth: 200}}
                  >
                    <option value="">{savedViewsLoading ? 'Loading...' : 'Select a saved view'}</option>
                    {myViews.length > 0 && (
                      <optgroup label="My views">
                        {myViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name + (v.visibility === 'public' ? ' - public' : ' - private')}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {publicOtherViews.length > 0 && (
                      <optgroup label="Public views">
                        {publicOtherViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {selectedViewIsMine && (
                    <>
                      <Button
                        variant="secondary"
                        type="button"
                        data-cattle-weighins-saved-view-update
                        onClick={updateSelectedView}
                        disabled={savedViewBusy}
                      >
                        Update to current
                      </Button>
                      <Button
                        variant="danger"
                        type="button"
                        data-cattle-weighins-saved-view-delete
                        onClick={deleteSelectedView}
                        disabled={savedViewBusy}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                  <span style={{flex: 1}} />
                  {savedViewNotice && (
                    <span
                      style={{
                        fontSize: 12,
                        color: savedViewNotice.kind === 'success' ? 'var(--ok-ink)' : 'var(--danger)',
                      }}
                    >
                      {savedViewNotice.message}
                    </span>
                  )}
                  <Button
                    variant="secondary"
                    type="button"
                    data-cattle-weighins-saved-view-save-open
                    onClick={openSaveViewForm}
                    disabled={savedViewBusy || savedViewsLoading}
                  >
                    Save current view
                  </Button>
                </>
              )}
            </div>
            {showSaveViewForm && (
              <div
                data-cattle-weighins-saved-view-form
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '10px 14px',
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <input
                  data-cattle-weighins-saved-view-name
                  type="text"
                  value={saveViewName}
                  placeholder="View name"
                  onChange={(e) => setSaveViewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitSaveView();
                  }}
                  style={{...savedViewInputS, flex: 1, minWidth: 200}}
                />
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveCattleWeighInsViewVisibility"
                    checked={saveViewVisibility === 'private'}
                    onChange={() => setSaveViewVisibility('private')}
                    data-cattle-weighins-saved-view-visibility="private"
                  />
                  Private
                </label>
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveCattleWeighInsViewVisibility"
                    checked={saveViewVisibility === 'public'}
                    onChange={() => setSaveViewVisibility('public')}
                    data-cattle-weighins-saved-view-visibility="public"
                  />
                  Public
                </label>
                <Button
                  variant="secondary"
                  type="button"
                  data-cattle-weighins-saved-view-save
                  onClick={submitSaveView}
                  disabled={savedViewBusy}
                >
                  Save
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setShowSaveViewForm(false)}
                  disabled={savedViewBusy}
                >
                  Cancel
                </Button>
              </div>
            )}
          </>
        )}
        <div
          className="cattle-weighins-page__heading"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div>
            <h1 className="cattle-page-title">Weigh-ins</h1>
            <div className="cattle-page-summary">
              <RuminantWeighInsHeaderSummary
                tagQ={tagQ}
                tagSearch={tagSearch}
                matchedSessionCount={matchedSessionCount}
                sessionCount={filtered.length}
                totalEntries={totalEntries}
                emphasizeCounts
              />
            </div>
          </div>
          <div style={{display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap'}}>
            {EXTENDED_LIST_CONTROLS_ENABLED && (
              <>
                <div style={{position: 'relative'}}>
                  <input
                    type="search"
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    placeholder="Search by tag #..."
                    style={{
                      fontFamily: 'inherit',
                      fontSize: 12,
                      padding: '6px 28px 6px 10px',
                      border: '1px solid var(--border-strong)',
                      borderRadius: 10,
                      width: 160,
                      boxSizing: 'border-box',
                      background: 'var(--surface)',
                      color: 'var(--ink)',
                      outline: 'none',
                    }}
                  />
                  {tagSearch && (
                    <button
                      type="button"
                      onClick={() => setTagSearch('')}
                      title="Clear search"
                      style={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        fontSize: 16,
                        lineHeight: 1,
                        color: 'var(--ink-faint)',
                        cursor: 'pointer',
                        padding: '2px 6px',
                        fontFamily: 'inherit',
                      }}
                    >
                      {'×'}
                    </button>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    borderRadius: 10,
                    overflow: 'hidden',
                    border: '1px solid var(--border-strong)',
                  }}
                >
                  {[
                    {k: 'all', l: 'All'},
                    {k: 'draft', l: 'Drafts'},
                    {k: 'complete', l: 'Complete'},
                  ].map((o, oi) => (
                    <button
                      key={o.k}
                      onClick={() => setStatusFilter(o.k)}
                      style={{
                        padding: '5px 10px',
                        border: 'none',
                        borderRight: oi < 2 ? '1px solid var(--border-strong)' : 'none',
                        fontFamily: 'inherit',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: 'var(--surface)',
                        color: statusFilter === o.k ? getProgramColor('cattle') : 'var(--ink-muted)',
                      }}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
                <Button
                  variant="secondary"
                  type="button"
                  data-cattle-weighins-export-csv="1"
                  onClick={handleExportCsv}
                  disabled={loading || loadFailed}
                >
                  Export CSV
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  data-cattle-weighins-print="1"
                  onClick={handlePrintRows}
                  disabled={loading || loadFailed}
                >
                  Print
                </Button>
              </>
            )}
            <Button
              variant="primary"
              type="button"
              className="cattle-button cattle-button-primary"
              data-new-weighin-button="1"
              onClick={() => setShowNewModal(true)}
            >
              <span aria-hidden="true">+</span>
              <span>New weigh-in</span>
            </Button>
          </div>
        </div>

        {loading && <div style={{textAlign: 'center', padding: '2rem', color: 'var(--ink-faint)'}}>Loading{'…'}</div>}
        {!loading && !loadFailed && filtered.length === 0 && (
          <div
            data-weighin-empty-state="1"
            data-weighin-empty-kind={emptyStateKind}
            style={ruminantWeighInEmptyBoxStyle}
          >
            <div style={{fontWeight: 700, color: 'var(--ink)', marginBottom: 4}}>{emptyStateMessage}</div>
            <div>{emptyStateHint}</div>
          </div>
        )}

        {!loadFailed && filtered.length > 0 && (
          <section className="cattle-card cattle-weighins-list">
            <DataTable
              surfaceKey="cattle-weighins-table"
              rows={filtered}
              rowKey="id"
              density="comfortable"
              rowProps={(s) => ({'data-weighin-session-tile': s.id})}
              // Phone card (Table G): herd and date; team and entries; ADG and
              // days since last; an open session's status as the one badge.
              card={{
                main: (s) => (HERD_LABELS[s.herd] || s.herd || 'Unknown herd') + ' · ' + fmtDate(s.date),
                line1: (s) => {
                  const all = entries[s.id] || [];
                  const shown = ruminantWeighInEntryMatchText(all, tagQ, entryMatchesTag);
                  return [s.team_member, shown].filter(Boolean).join(' · ');
                },
                line2: (s) => {
                  const m = cattleWeighInListMetrics(listMetrics, s.id);
                  const days = formatDaysSinceLastWeighIn(m.daysSinceLastWeighIn);
                  return [formatAvgAdg(m.avgAdgLbsPerDay), days ? days + ' since last' : null]
                    .filter(Boolean)
                    .join(' · ');
                },
                badge: (s) => (s.status && s.status !== 'complete' ? <Badge status={s.status} /> : null),
              }}
              onRowOpen={(s) =>
                navigate(
                  '/weigh-in-sessions/' + s.id,
                  recordSeqNavOptions(
                    filtered.map((r) => ({
                      id: r.id,
                      label: (r.date || '') + ' · ' + (HERD_LABELS[r.herd] || r.herd || 'cattle'),
                    })),
                  ),
                )
              }
              columns={[
                {key: 'date', label: 'Date', primary: true, render: (s) => fmt(s.date)},
                {
                  key: 'herd',
                  label: 'Herd',
                  // R06: the herd's 7px group dot, the same palette slot the
                  // herds list gives it (index in CATTLE_HERD_KEYS).
                  render: (s) => (
                    <span className="cattle-cell-primary">
                      <RuminantGroupLabel
                        label={HERD_LABELS[s.herd] || s.herd || 'Unknown herd'}
                        index={CATTLE_HERD_KEYS.indexOf(s.herd)}
                      />
                    </span>
                  ),
                },
                {
                  key: 'status',
                  label: 'Status',
                  render: (s) => <Badge status={s.status || 'Unknown'} />,
                },
                {
                  key: 'team',
                  label: 'Team',
                  render: (s) => s.team_member || <StatusText tone="muted">{'—'}</StatusText>,
                },
                {
                  key: 'count',
                  label: 'Entries',
                  render: (s) => {
                    const sEntriesAll = entries[s.id] || [];
                    if (tagQ) {
                      return (
                        <StatusText tone="ok">
                          {sEntriesAll.filter(entryMatchesTag).length + ' of ' + sEntriesAll.length + ' match'}
                        </StatusText>
                      );
                    }
                    return <strong>{sEntriesAll.length}</strong>;
                  },
                },
                // The two derived metrics replace the former New tags column.
                // `new_tag_flag` itself, the reconciliation rules that gate
                // completion, the session record page's NEW TAG affordances and
                // the CSV/print "New tag count" are all untouched.
                {
                  key: 'daysSinceLastWeighIn',
                  label: 'Since last',
                  render: (s) => {
                    const text = formatDaysSinceLastWeighIn(
                      cattleWeighInListMetrics(listMetrics, s.id).daysSinceLastWeighIn,
                    );
                    if (!text) return <StatusText tone="muted">{'—'}</StatusText>;
                    return text;
                  },
                },
                {
                  key: 'avgAdg',
                  label: 'Avg ADG',
                  render: (s) => {
                    const m = cattleWeighInListMetrics(listMetrics, s.id);
                    const value = m.avgAdgLbsPerDay;
                    const text = formatAvgAdg(value);
                    if (!text) {
                      return (
                        <StatusText tone="muted" title={avgAdgProvenance(m)}>
                          {'—'}
                        </StatusText>
                      );
                    }
                    return <strong title={avgAdgProvenance(m)}>{text}</strong>;
                  },
                },
              ]}
            />
          </section>
        )}
      </div>
      {showNewModal && (
        <CattleNewWeighInModal
          authState={lockedAuthState}
          onClose={() => setShowNewModal(false)}
          onCreate={createNewSession}
        />
      )}
    </div>
  );
};

export default CattleWeighInsView;
